import { List, ListItemText, Menu, MenuItem, Paper, Popper } from '@mui/material';
import { useRef, useState } from 'react';

type PlaceholderItem = string;
type PlaceholderGroup = {
  label: string;
  items: (PlaceholderItem | PlaceholderGroup)[];
};

type Props = {
  anchorEl: HTMLElement | null;
  position: { top: number; left: number } | null;
  onClose: () => void;
  placeholders: (PlaceholderItem | PlaceholderGroup)[];
  onInsert: (placeholder: string) => void;
};

const ContextMenuWithSubmenu = ({ anchorEl, position, onClose, placeholders, onInsert }: Props) => {
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [submenuItems, setSubmenuItems] = useState<(PlaceholderItem | PlaceholderGroup)[] | null>(null);
  const submenuTimer = useRef<NodeJS.Timeout | null>(null);

  const handleCloseSubmenu = () => {
    setSubmenuItems(null);
    setSubmenuAnchor(null);
  };

  const renderMenuItems = (items: (PlaceholderItem | PlaceholderGroup)[]) =>
    items.map((item) => {
      if (typeof item === 'string') {
        return (
          <MenuItem key={item} onClick={() => onInsert(item)}>
            {item}
          </MenuItem>
        );
      }

      return (
        <MenuItem
          key={item.label}
          onMouseEnter={(e) => {
            if (submenuTimer.current) clearTimeout(submenuTimer.current);
            setSubmenuAnchor(e.currentTarget);
            setSubmenuItems(item.items);
          }}
          onMouseLeave={() => {
            submenuTimer.current = setTimeout(() => {
              handleCloseSubmenu();
            }, 300);
          }}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
        >
          <ListItemText primary={item.label} />
          <span>›</span>
        </MenuItem>
      );
    });

  return (
    <>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={position || undefined}
        disableAutoFocusItem
        disableEnforceFocus
        disableRestoreFocus
      >
        {renderMenuItems(placeholders)}
      </Menu>

      <Popper
        open={!!submenuAnchor && !!submenuItems}
        anchorEl={submenuAnchor}
        placement="right-start"
        style={{ zIndex: 1300 }}
        onMouseEnter={() => {
          if (submenuTimer.current) clearTimeout(submenuTimer.current);
        }}
        onMouseLeave={() => {
          submenuTimer.current = setTimeout(() => {
            handleCloseSubmenu();
          }, 200);
        }}
      >
        <Paper elevation={3}>
          <List>
            {submenuItems?.map((item) =>
              typeof item === 'string' ? (
                <MenuItem key={item} onClick={() => onInsert(item)}>
                  {item}
                </MenuItem>
              ) : (
                <MenuItem key={item.label} disabled>
                  {item.label} ›
                </MenuItem>
              )
            )}
          </List>
        </Paper>
      </Popper>
    </>
  );
};

export default ContextMenuWithSubmenu;
